import { GitPullRequest, GitMerge, GitPullRequestClosed } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { PrState, Priority, TaskType } from "../mock/types";
import { priorityLabel } from "../mock/helpers";

export function PriorityBadge({ priority }: { priority: Priority }) {
	return (
		<Badge variant={priority} className="text-[10px]">
			{priorityLabel(priority)}
		</Badge>
	);
}

export function TicketCode({ code }: { code: string }) {
	return (
		<span className="text-[10px] bg-muted-subtle text-muted-foreground px-1.5 py-0.5 rounded font-medium leading-none font-mono">
			#{code}
		</span>
	);
}

export function Tag({ label }: { label: string }) {
	return (
		<span className="text-[10px] bg-muted-subtle text-muted-foreground px-1.5 py-0.5 rounded font-medium leading-none">
			{label}
		</span>
	);
}

export function TypeChip({
	type,
	className,
}: {
	type: TaskType;
	className?: string;
}) {
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 text-[11px] font-medium leading-none",
				className,
			)}
			style={{ color: type.color }}
			title={type.name}
		>
			<span
				className="h-2 w-2 rounded-full"
				style={{ backgroundColor: type.color }}
			/>
			{type.name}
		</span>
	);
}

const PR_META: Record<
	Exclude<PrState, "none">,
	{ label: string; className: string; Icon: typeof GitPullRequest }
> = {
	open: {
		label: "Open",
		className: "text-secondary border-secondary/30 bg-secondary-subtle",
		Icon: GitPullRequest,
	},
	draft: {
		label: "Draft",
		className: "text-muted border-border bg-muted-subtle",
		Icon: GitPullRequest,
	},
	merged: {
		label: "Merged",
		className: "text-accent border-accent/30 bg-accent-subtle",
		Icon: GitMerge,
	},
	closed: {
		label: "Closed",
		className: "text-danger border-danger/30 bg-danger-subtle",
		Icon: GitPullRequestClosed,
	},
};

export function PrBadge({
	state,
	prNumber,
}: {
	state: PrState;
	prNumber: number | null;
}) {
	if (state === "none") return null;
	const meta = PR_META[state];
	const Icon = meta.Icon;
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none",
				meta.className,
			)}
		>
			<Icon className="h-3 w-3" />
			{prNumber ? `#${prNumber}` : meta.label}
		</span>
	);
}
